import React, { useState } from 'react';
import { useUpload } from '../../context/UploadContext';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

const BackgroundTasks = () => {
    const { tasks, removeTask } = useUpload();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);

    const handleRetry = (task) => {
        let payload = task.payload;
        if (typeof payload === 'string') {
            try { payload = JSON.parse(payload); } catch (e) { }
        }

        if (task.task_type === 'CREATE') {
            navigate('/admin/create-product', { state: { retryData: payload } });
        } else {
            const editId = task.task_id || (payload && payload.id);
            navigate(`/admin/edit-product/${editId}`, { state: { retryData: payload } });
        }
        setIsExpanded(false);
    };

    if (tasks.length === 0) return null;

    const activeCount = tasks.filter(t => t.status === 'running' || t.status === 'pending').length;
    const errorCount = tasks.filter(t => t.status === 'error').length;

    return (
        <div className={`fixed bottom-6 right-6 z-[9999] transition-all duration-500 ease-out ${isExpanded ? 'w-80 lg:w-96' : 'w-16 lg:w-64'}`}>
            {/* Minimal Toggle Bubble */}
            {!isExpanded && (
                <button
                    onClick={() => setIsExpanded(true)}
                    className="w-16 h-16 bg-white border-2 border-primary-500 rounded-full shadow-2xl flex items-center justify-center relative hover:scale-105 transition-transform"
                >
                    <i className={`fas ${activeCount > 0 ? 'fa-spinner fa-spin text-primary-600' : 'fa-tasks text-gray-400'} text-xl`}></i>
                    {activeCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full ring-2 ring-white">
                            {activeCount}
                        </span>
                    )}
                    {errorCount > 0 && (
                        <span className="absolute -bottom-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full ring-2 ring-white">
                            {errorCount}
                        </span>
                    )}
                </button>
            )}

            {/* Expanded List */}
            {isExpanded && (
                <div className="bg-white rounded-[30px] border border-gray-100 shadow-2xl flex flex-col overflow-hidden max-h-[500px]">
                    <div className="p-5 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-primary-100 p-2.5 rounded-2xl text-primary-600">
                                <i className="fas fa-layer-group text-sm"></i>
                            </div>
                            <div>
                                <h4 className="font-black text-gray-900 text-sm">{t("admin.tasks.title", "Tasks")}</h4>
                                <Link to="/admin/tasks" onClick={() => setIsExpanded(false)} className="text-[10px] font-bold text-brand-primary hover:underline uppercase tracking-widest">{t("admin.tasks.history", "View History")}</Link>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{activeCount} {t("admin.tasks.active", "Active")}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsExpanded(false)}
                            className="text-gray-400 hover:text-gray-600 p-2 transition-colors"
                        >
                            <i className="fas fa-chevron-down"></i>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                        {tasks.map((task) => (
                            <div key={task.id} className="group p-4 bg-gray-50/50 rounded-2xl border border-gray-100/50 hover:border-primary-100 transition-colors relative overflow-hidden">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`w-1.5 h-1.5 rounded-full ${task.status === 'success' ? 'bg-green-500' :
                                                task.status === 'error' ? 'bg-red-500' :
                                                    task.status === 'interrupted' ? 'bg-yellow-500' :
                                                        'bg-primary-500 animate-pulse'
                                                }`}></span>
                                            <h5 className="font-bold text-gray-900 text-xs truncate">{task.name}</h5>
                                        </div>
                                        <p className="text-[10px] text-gray-500 font-medium truncate">{task.message}</p>
                                    </div>
                                    {task.status !== 'running' && (
                                        <button onClick={() => removeTask(task.id)} className="text-gray-300 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <i className="fas fa-times text-[10px]"></i>
                                        </button>
                                    )}
                                </div>

                                {task.status === 'running' && (
                                    <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-brand-primary transition-all duration-500 relative overflow-hidden"
                                            style={{ width: `${task.progress}%` }}
                                        >
                                            <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                                        </div>
                                    </div>
                                )}

                                {task.status === 'success' && (
                                    <div className="flex items-center gap-1.5 text-[10px] font-black text-green-600 uppercase tracking-tight">
                                        <i className="fas fa-check-circle"></i>
                                        {t("admin.tasks.completed", "Completed")}
                                    </div>
                                )}

                                {(task.status === 'error' || task.status === 'interrupted') && (
                                    <div className="flex items-center justify-between mt-2">
                                        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-tight">
                                            <i className={`fas ${task.status === 'error' ? 'fa-exclamation-circle text-red-600' : 'fa-pause-circle text-yellow-500'}`}></i>
                                            <span className={task.status === 'error' ? 'text-red-600' : 'text-yellow-600'}>
                                                {task.status === 'error' ? t("admin.tasks.failed", "Failed") : t("admin.tasks.interrupted", "Interrupted")}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleRetry(task)}
                                            className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest transition-colors flex items-center gap-1 ${task.status === 'error'
                                                    ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                                    : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                                                }`}
                                        >
                                            <i className="fas fa-redo text-[8px]"></i>
                                            {t("admin.tasks.retry", "Retry")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BackgroundTasks;
